import { createRecordId } from "./utils";
import { getRichTextPlainText, normalizeRichTextContent } from "./richText";
import type { RichTextContent, TicketNote, TicketNoteDeliveryStatus, TicketNoteKind } from "./types";

const LEGACY_NOTE_SPLIT_REGEX = /\n\n(?=\[[^\]]+\]\s)/;
const LEGACY_NOTE_HEADER_REGEX = /^\[([^\]]+)\]\s+(.*)$/;

function normalizeText(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeTicketNoteKind(value: unknown): TicketNoteKind {
  return value === "external" ? "external" : "internal";
}

function normalizeTicketNoteDeliveryStatus(value: unknown): TicketNoteDeliveryStatus | null {
  switch (value) {
    case "pending":
    case "sent":
    case "failed":
    case "received":
      return value;
    default:
      return null;
  }
}

function normalizeStoredTicketNote(value: unknown, index: number): TicketNote | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Partial<TicketNote>;
  const bodyContent = normalizeRichTextContent(record.bodyContent);
  const body = normalizeText(record.body) || getRichTextPlainText(bodyContent);

  if (!body) {
    return null;
  }

  const kind = normalizeTicketNoteKind(record.kind);
  const createdAt = normalizeText(record.createdAt);
  const legacyTimestampLabel = normalizeText(record.legacyTimestampLabel);

  return {
    id: normalizeText(record.id) || `ticket-note-${index + 1}`,
    author: normalizeText(record.author) || "Case note",
    body,
    bodyContent,
    createdAt: createdAt || null,
    kind,
    deliveryStatus: kind === "external"
      ? normalizeTicketNoteDeliveryStatus(record.deliveryStatus) ?? "pending"
      : null,
    isLegacy: Boolean(record.isLegacy),
    legacyTimestampLabel: legacyTimestampLabel || null,
  };
}

function parseSerializedTicketNotes(value: string) {
  try {
    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) {
      return null;
    }

    return parsed
      .map((note, index) => normalizeStoredTicketNote(note, index))
      .filter((note): note is TicketNote => note !== null);
  } catch {
    return null;
  }
}

function parseLegacyTicketNotes(value: string): TicketNote[] {
  return value
    .split(LEGACY_NOTE_SPLIT_REGEX)
    .filter(Boolean)
    .map((block, index) => {
      const [headerLine = "", ...bodyLines] = block.split("\n");
      const headerMatch = headerLine.match(LEGACY_NOTE_HEADER_REGEX);

      if (!headerMatch) {
        return {
          id: `legacy-ticket-note-${index + 1}`,
          author: "Case note",
          body: block.trim(),
          createdAt: null,
          kind: "internal" as const,
          deliveryStatus: null,
          isLegacy: true,
          legacyTimestampLabel: null,
        };
      }

      return {
        id: `legacy-ticket-note-${index + 1}`,
        author: normalizeText(headerMatch[2]) || "Case note",
        body: bodyLines.join("\n").trim(),
        createdAt: null,
        kind: "internal" as const,
        deliveryStatus: null,
        isLegacy: true,
        legacyTimestampLabel: normalizeText(headerMatch[1]) || null,
      };
    })
    .filter(note => note.body);
}

export function parseTicketNotes(notes: string): TicketNote[] {
  const normalizedNotes = notes.trim();

  if (!normalizedNotes) {
    return [];
  }

  return parseSerializedTicketNotes(normalizedNotes) ?? parseLegacyTicketNotes(normalizedNotes);
}

export function serializeTicketNotes(notes: TicketNote[]) {
  if (notes.length === 0) {
    return "";
  }

  return JSON.stringify(notes.map((note, index) => {
    const normalizedNote = normalizeStoredTicketNote(note, index);

    return {
      id: normalizedNote?.id ?? `ticket-note-${index + 1}`,
      author: normalizedNote?.author ?? "Case note",
      body: normalizedNote?.body ?? "",
      bodyContent: normalizedNote?.bodyContent ?? null,
      createdAt: normalizedNote?.createdAt ?? null,
      kind: normalizedNote?.kind ?? "internal",
      deliveryStatus: normalizedNote?.kind === "external"
        ? normalizedNote.deliveryStatus ?? "pending"
        : null,
      isLegacy: normalizedNote?.isLegacy ?? false,
      legacyTimestampLabel: normalizedNote?.legacyTimestampLabel ?? null,
    };
  }));
}

export function appendTicketNote(
  existingNotes: string,
  input: {
    author: string;
    body: string;
    bodyContent?: RichTextContent;
    kind?: TicketNoteKind;
    createdAt?: string | null;
    deliveryStatus?: TicketNoteDeliveryStatus | null;
    id?: string;
  },
) {
  const bodyContent = normalizeRichTextContent(input.bodyContent);
  const body = normalizeText(input.body) || getRichTextPlainText(bodyContent);

  if (!body) {
    return {
      notes: existingNotes.trim(),
      note: null,
    };
  }

  const kind = normalizeTicketNoteKind(input.kind);
  const note: TicketNote = {
    id: normalizeText(input.id) || createRecordId("ticket-note"),
    author: normalizeText(input.author) || "Case note",
    body,
    bodyContent,
    createdAt: normalizeText(input.createdAt) || new Date().toISOString(),
    kind,
    deliveryStatus: kind === "external"
      ? normalizeTicketNoteDeliveryStatus(input.deliveryStatus) ?? "pending"
      : null,
    isLegacy: false,
    legacyTimestampLabel: null,
  };

  return {
    notes: serializeTicketNotes([note, ...parseTicketNotes(existingNotes)]),
    note,
  };
}

export function getAddedTicketNotes(previousNotes: string, nextNotes: string) {
  const previousIds = new Set(parseTicketNotes(previousNotes).map(note => note.id));
  return parseTicketNotes(nextNotes).filter(note => !previousIds.has(note.id));
}

export function updateTicketNotesDeliveryStatus(
  existingNotes: string,
  noteIds: string[],
  deliveryStatus: TicketNoteDeliveryStatus,
) {
  if (!Array.isArray(noteIds) || noteIds.length === 0) {
    return existingNotes.trim();
  }

  const noteIdSet = new Set(noteIds.map(noteId => normalizeText(noteId)).filter(Boolean));

  if (noteIdSet.size === 0) {
    return existingNotes.trim();
  }

  const notes = parseTicketNotes(existingNotes);

  if (notes.length === 0) {
    return existingNotes.trim();
  }

  return serializeTicketNotes(notes.map(note => (
    note.kind === "external" && noteIdSet.has(note.id)
      ? { ...note, deliveryStatus }
      : note
  )));
}